/**
 * Captures whose End did not reach the server, sent again until one does.
 *
 * `closeSessionCaptureApi` retries a few times over a few seconds, which covers
 * a blip but not a facilitator who pressed End with the Wi-Fi gone. Until
 * 2026-10-28 that close was simply dropped: the capture stayed live until the
 * four-hour sweep, and its summary waited with it (test day N10). Now it is kept
 * here and sent again when the device is back online, and the next time the app
 * opens if the tab was closed first.
 *
 * Kept in `localStorage` so it survives the tab, and best-effort like any
 * browser storage: the four-hour sweep is still there behind it.
 */

import { closeSessionCaptureApi } from './api'

const KEY = 'lorefully.pendingCaptureCloses'

/** Past this, the four-hour sweep has long since ended it; stop sending. */
const GIVE_UP_MS = 24 * 60 * 60 * 1000

interface Pending {
    id: string
    queuedAt: number
}

function read(): Pending[] {
    try {
        const items = JSON.parse(localStorage.getItem(KEY) ?? '[]')
        if (!Array.isArray(items)) return []
        return items.filter(
            (p): p is Pending =>
                typeof p?.id === 'string' && typeof p?.queuedAt === 'number'
        )
    } catch {
        return []
    }
}

function write(items: Pending[]) {
    try {
        if (items.length) localStorage.setItem(KEY, JSON.stringify(items))
        else localStorage.removeItem(KEY)
    } catch {
        // Storage refused (private mode, full); the sweep still ends it.
    }
}

const without = (items: Pending[], id: string) =>
    items.filter((p) => p.id !== id)

/** Remember an End that did not get through, to send again later. */
export function queueCaptureClose(contentSessionId: string) {
    const items = read()
    if (items.some((p) => p.id === contentSessionId)) return
    write([...items, { id: contentSessionId, queuedAt: Date.now() }])
}

/** The Ends still waiting to get through. */
export const pendingCaptureCloses = (): string[] => read().map((p) => p.id)

let flushing = false

/** Send every waiting End once. Those that fail stay for next time. */
export async function flushCaptureCloses(): Promise<void> {
    if (flushing) return
    flushing = true
    try {
        for (const { id, queuedAt } of read()) {
            if (Date.now() - queuedAt > GIVE_UP_MS) {
                write(without(read(), id))
                continue
            }
            try {
                await closeSessionCaptureApi(id, { completeSession: true })
                write(without(read(), id))
            } catch {
                // Still unreachable, or refused for now; keep it.
            }
        }
    } finally {
        flushing = false
    }
}

/** Send waiting Ends now and whenever the device comes back online. */
export function watchCaptureCloses(): () => void {
    const flush = () => void flushCaptureCloses()
    window.addEventListener('online', flush)
    if (read().length) flush()
    return () => window.removeEventListener('online', flush)
}
